import { ApolloLink } from '@apollo/client'
import { Observable } from 'rxjs'

const getAbortSignal = (
  operation?: ApolloLink.Operation,
): AbortSignal | undefined => operation?.getContext().fetchOptions?.signal

const createAbortError = (): DOMException =>
  new DOMException('The operation was aborted.', 'AbortError')

export const isRequestCancelled = (
  error: unknown,
  operation?: ApolloLink.Operation,
): boolean =>
  (error instanceof Error && error.name === 'AbortError') ||
  getAbortSignal(operation)?.aborted === true

// Signals are single-use. A watched query must receive a fresh signal in its
// context before it can refetch after cancellation.
export const RequestCancellationLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const signal = getAbortSignal(operation)
      let downstreamSubscription: { unsubscribe: () => void } | undefined

      const cancel = (): void => {
        observer.error(createAbortError())
        downstreamSubscription?.unsubscribe()
      }

      observer.add(() => {
        signal?.removeEventListener('abort', cancel)
        downstreamSubscription?.unsubscribe()
      })
      signal?.addEventListener('abort', cancel, { once: true })

      if (signal?.aborted) {
        cancel()
        return
      }

      downstreamSubscription = forward(operation).subscribe({
        next: (result) => observer.next(result),
        error: (error) => observer.error(error),
        complete: () => observer.complete(),
      })

      if (observer.closed) {
        downstreamSubscription.unsubscribe()
      }
    }),
)

// RetryLink can schedule after its awaited retry decision resolves, even when
// the outer subscription was cancelled in the meantime. Guard every attempt.
export const AbortGuardLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      if (getAbortSignal(operation)?.aborted) {
        observer.error(createAbortError())
        return
      }

      return forward(operation).subscribe(observer)
    }),
)
